import { useMemo } from 'preact/hooks';
import { RETURN_REQUEST_LEVEL } from '@pperform/pp-constants/widgetConfigs';
import { usePTPConfigContext } from 'GlobalConfig.context';
import type Shipment from 'interfaces/PmParcel';

import { CustomText } from 'components/CustomText';
import { Stack } from 'components/Stack';
import { CommentInput } from '../CommentInput';
import { UploadSection } from '../UploadSection';
import { LineItems } from '../LineItems';
import { OrderLineItems } from '../OrderLineItems';
import type {
  ReturnLineItem,
  ReturnRequestError,
  ReturnSubmissionFormData,
  AttachmentRef,
  ReturnReason,
} from '../../../../interfaces/ReturnRequest.interface';

import { CustomTextContainer } from './EditMode.styles';

interface EditModeProps {
  shipment: Shipment;
  returnData: ReturnSubmissionFormData;
  attachmentRef: React.MutableRefObject<AttachmentRef>;
  errors?: ReturnRequestError;
  onCheckLineItems: (returnItem: ReturnLineItem, key: string) => void;
  onUncheckLineItems: (key: string) => void;
  onSelectItemQuantity: (quantity: number, key: string) => void;
  onSelectReasonForReturn: (reason: ReturnReason, key: string) => void;
  onChangeCustomReasonForReturn: (reason: string, key: string) => void;
  onUpdateComment: (comment: string) => void;
  /**
   * ID of the form title element for aria association
   */
  formTitleId?: string;
}

export const EditMode = ({
  shipment,
  returnData,
  attachmentRef,
  errors,
  onCheckLineItems,
  onUncheckLineItems,
  onSelectItemQuantity,
  onSelectReasonForReturn,
  onChangeCustomReasonForReturn,
  onUpdateComment,
  formTitleId,
}: EditModeProps) => {
  const {
    return: { customText, returnRequestScope, formSection },
  } = usePTPConfigContext();

  const { hideUpload, hideComment } = formSection || {};

  const isOrderLevel =
    returnRequestScope?.returnLevel === RETURN_REQUEST_LEVEL.ORDER;

  const displayLineItems = useMemo(() => {
    if (isOrderLevel) {
      return (
        <OrderLineItems orderId={shipment.orderId} shipment={shipment}>
          <LineItems
            errors={errors?.lineItems}
            shipmentLineItems={shipment.lineItems}
            selectedLineItems={returnData.selectedLineItems}
            onChangeReturnQuantity={onSelectItemQuantity}
            onChangeCustomReasonForReturn={onChangeCustomReasonForReturn}
            onChangeReasonForReturn={onSelectReasonForReturn}
            onSelectLineItem={onCheckLineItems}
            onDeselectLineItem={onUncheckLineItems}
            formTitleId={formTitleId}
          />
        </OrderLineItems>
      );
    }

    if (!shipment.lineItems) {
      return null;
    }

    return (
      <LineItems
        errors={errors?.lineItems}
        shipmentLineItems={shipment.lineItems}
        selectedLineItems={returnData.selectedLineItems}
        onChangeReturnQuantity={onSelectItemQuantity}
        onChangeCustomReasonForReturn={onChangeCustomReasonForReturn}
        onChangeReasonForReturn={onSelectReasonForReturn}
        onSelectLineItem={onCheckLineItems}
        onDeselectLineItem={onUncheckLineItems}
        formTitleId={formTitleId}
      />
    );
  }, [
    shipment,
    returnData.selectedLineItems,
    errors?.lineItems,
    isOrderLevel,
    onSelectItemQuantity,
    onSelectReasonForReturn,
    onCheckLineItems,
    onUncheckLineItems,
    formTitleId,
  ]);

  return (
    <Stack direction="column" gap={16} align="stretch">
      {displayLineItems}
      {!hideUpload && (
        <UploadSection
          ref={attachmentRef}
          shipmentPk={shipment.poId}
          uploadedAttachments={attachmentRef.current?.getAttachments()}
        />
      )}
      {!hideComment && (
        <CommentInput
          comment={returnData?.comment}
          onUpdateComment={onUpdateComment}
        />
      )}
      <CustomTextContainer direction="column" gap={8}>
        {shipment.lineItems?.some((lineItem) => !lineItem.eligible) && (
          <CustomText text={customText.nonEligibleAdditionalDetails?.text} />
        )}
        <CustomText text={customText.returnForm?.text} />
      </CustomTextContainer>
    </Stack>
  );
};
